import { useQuery } from "@tanstack/react-query";

export interface ProductTypeImage {
  id: string;
  productType: string;
  displayName: string;
  imageUrl: string | null;
  status: string;
  generatedAt: Date | null;
}

export function useProductTypeImages() {
  return useQuery<ProductTypeImage[]>({
    queryKey: ['/api/product-type-images'],
    staleTime: 1000 * 60 * 30, // 30 minutes
  });
}

export function inferProductType(description: string, brand: string): string | null {
  const desc = description.toLowerCase();
  const brandLower = brand.toLowerCase();
  
  // Johnson Fitness brands
  const isJohnsonFitness = ['bowflex', 'schwinn', 'nautilus', 'horizon', 'johnson'].some(
    b => brandLower.includes(b)
  );
  
  if (!isJohnsonFitness) return null;
  
  // Treadmills
  if (desc.includes('treadmill') || /\b(t10|t16|t22|bxt)\b/i.test(desc)) {
    return 'treadmill';
  }
  
  // Max Trainers (Bowflex specific)
  if (desc.includes('max trainer') || desc.includes('max total')) {
    return 'max-trainer';
  }
  
  // Ellipticals
  if (desc.includes('elliptical') || (brandLower === 'schwinn' && /\b(130|170|430|470)\b/.test(desc))) {
    return 'elliptical';
  }
  
  // Spin/IC Bikes
  if (desc.includes('spin') || desc.includes('ic bike') || desc.includes('velocore') || /\b(c6|c7)\b/i.test(desc)) {
    return 'spin-bike';
  }
  
  // Recumbent Bikes
  if (desc.includes('recumbent') || (brandLower === 'schwinn' && /\b(230|270|290)\b/.test(desc))) {
    return 'recumbent-bike';
  }
  
  // Upright Bikes
  if (desc.includes('upright') || (brandLower === 'schwinn' && /\b(130|170|190)\b/.test(desc))) {
    return 'upright-bike';
  }
  
  // Home Gyms
  if (desc.includes('home gym') || /\b(pr1000|pr3000|xceed)\b/i.test(desc)) {
    return 'home-gym';
  }
  
  // Weight Benches
  if (desc.includes('bench') && (desc.includes('weight') || /\b[345]\.1s\b/i.test(desc))) {
    return 'weight-bench';
  }
  
  // Rowers
  if (desc.includes('rower') || desc.includes('rowing')) {
    return 'rower';
  }
  
  // Kettlebells
  if (desc.includes('kettlebell')) {
    return 'kettlebell';
  }
  
  return null;
}

export function getProductFallbackImage(
  description: string,
  brand: string,
  productTypeImages: ProductTypeImage[] | undefined
): string | null {
  const productType = inferProductType(description, brand);
  
  if (!productType || !productTypeImages) {
    return null;
  }
  
  const matchingImage = productTypeImages.find(
    img => img.productType === productType && img.imageUrl
  );
  
  return matchingImage?.imageUrl || null;
}

const DEFAULT_PLACEHOLDER = "https://images.unsplash.com/photo-1571175443880-49e1d25b2bc5?w=800&h=600&fit=crop";

export function resolveProductImage(
  imageUrl: string | undefined | null,
  description: string,
  brand: string,
  productTypeImages: ProductTypeImage[] | undefined
): string {
  // If product has its own image, use it
  if (imageUrl) {
    return imageUrl;
  }
  
  // Try to get a product type fallback image
  const fallbackImage = getProductFallbackImage(description, brand, productTypeImages);
  if (fallbackImage) {
    return fallbackImage;
  }
  
  // Default placeholder
  return DEFAULT_PLACEHOLDER;
}
